import React, { useState, useEffect, useRef } from 'react';
import { Button, Title } from '@bsf/force-ui';
import { __ } from '@wordpress/i18n';
import apiFetch from '@wordpress/api-fetch';
import { ExternalLink, SearchIcon, Trash2, Upload } from "lucide-react";

const IconManager = () => {
    const [fonts, setFonts] = useState({});
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const sliderRefs = useRef({});
    const capitalize = ( str ) => str.charAt(0).toUpperCase() + str.slice(1);

    const fetchFonts = () => {
        apiFetch({
            path: '/ultimate-vc/v1/icon-fonts',
            headers: { 'X-WP-Nonce': uavcSettingsData.nonce },
        }).then((response) => {
            setFonts(response);
        });
    };

    useEffect(() => {
        fetchFonts();
    }, []);

    useEffect(() => {
        Object.values(fonts).forEach((font) => {
            if (!document.querySelector(`link[data-font="${font.name}"]`)) {
                const link = document.createElement('link');
                link.rel = 'stylesheet';
                link.href = font.style;
                link.dataset.font = font.name;
                document.head.appendChild(link);
            }
        });
    }, [fonts]);

    const uploadAttachment = (id) => {
        setLoading(true);
        apiFetch({
            path: '/ultimate-vc/v1/icon-fonts',
            method: 'POST',
            headers: { 'X-WP-Nonce': uavcSettingsData.nonce },
            data: { attachment: id },
        })
            .then(() => fetchFonts())
            .catch(() => fetchFonts())
            .finally(() => setLoading(false));
    };

    const openUploader = () => {
        if (typeof window.wp === 'undefined' || typeof window.wp.media === 'undefined') {
            console.error('WordPress Media Uploader is not available.');
            return;
        }

        const mediaUploader = window.wp.media({
            title: __('Select Icon Font Zip', 'ultimate_vc'),
            button: { text: __('Use this file', 'ultimate_vc') },
            multiple: false,
            library: { type: 'application/zip' },
        });

        mediaUploader.on('select', () => {
            const attachment = mediaUploader.state().get('selection').first().toJSON();
            uploadAttachment(attachment.id);
        });

        mediaUploader.open();
    };

    const handleDelete = (font) => {
        if (!window.confirm(__('Delete font?', 'ultimate_vc'))) {
            return;
        }
        apiFetch({
            path: `/ultimate-vc/v1/icon-fonts?font=${font}`,
            method: 'DELETE',
            headers: { 'X-WP-Nonce': uavcSettingsData.nonce },
        })
            .then(() => fetchFonts())
            .catch(() => fetchFonts());
    };

    return (
        <>
            <Title
                description=""
                icon={null}
                iconPosition="right"
                size="sm"
                tag="h2"
                title={__('Icon Manager', 'ultimate_vc')}
            />
            <div
                className="box-border bg-background-primary p-6 rounded-lg"
                style={{ marginTop: '24px', maxWidth: '90%' }}
            >
                <div className="flex items-center justify-start gap-6">
                    <input
                        type="text"
                        className="w-full border border-gray-300 rounded-md text-sm"
                        placeholder={__('Search icons', 'ultimate_vc')}
                        value={search}
                        style={{
                            borderColor: '#e0e0e0', // Default border color
                            outline: 'none',       // Removes the default outline
                            boxShadow: 'none',     // Removes the default box shadow
                            marginTop: '3px',
                            width: '34.75rem',
                            height: '46px',
                        }}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                    <div className='flex flex-col md:flex-row justify-around items-center gap-y-2  md:gap-x-6 md:mr-7 relative'>
                    <Upload
                        className="absolute top-1/2 transform -translate-y-1/2  text-gray-400"
                        style={{
                            backgroundColor: '#0017E1',
                            left: '2%',
                            color: '#fff',
                            width: '16px',
                            height: '16px',
                            padding: '10px',
                        }} />
                    <Button
                        variant="secondary"
                        size="sm"
                        disabled={loading}
                        onClick={openUploader}
                        className="text-white font-medium transition-all uavc-remove-ring rounded-md"
                        style={{ backgroundColor: '#0017E1', width: '130px',  height: '46px', marginTop: '3px', paddingLeft: '37px' }}
                    >
                        {__('Upload Icons', 'ultimate_vc')}
                    </Button>
                    </div>
                </div>

                {Object.keys(fonts).map((font) => {
                    const iconCount = fonts[font].icons ? fonts[font].icons.length : 0;
                    return (
                        <div key={font} className="mb-8">
                        <div className="flex items-center pt-10 justify-between mb-3">
                            <h4 className="text-base font-semibold text-gray-800">
                                {`${capitalize(font)} (${iconCount})`}
                            </h4>
                            {font !== 'Defaults' && (
                                <button
                                    onClick={() => handleDelete(font)}
                                    disabled={loading}
                                    className="text-red-500 hover:text-red-700 transition-colors"
                                    title="Delete Font"
                                >
                                    <Trash2 color={'red'} className="cursor-pointer"/>
                                </button>
                            )}
                        </div>

                        {/* Independent Scrollable Container */}
                        <div className="border border-gray-200 shadow-lg rounded-lg overflow-y-auto" style={{ maxHeight: '250px' }}>
                            <div
                                ref={(el) => {
                                    sliderRefs.current[font] = el;
                                }}
                                className="grid grid-cols-10 gap-4 p-4"
                                style={{
                                    scrollbarWidth: 'thin',
                                    scrollBehavior: 'smooth',
                                }}
                            >
                                {fonts[font].icons &&
                                    fonts[font].icons
                                        .filter((icon) => {
                                            if (!search) return true;
                                            const term = search.toLowerCase();
                                            return (
                                                icon.class.toLowerCase().includes(term) ||
                                                (icon.tags && icon.tags.toLowerCase().includes(term))
                                            );
                                        })
                                        .map((icon, index) => (
                                            <div
                                                key={index}
                                                className="w-10 h-10 border border-gray-300 rounded flex items-center justify-center hover:shadow-sm"
                                                title={icon.class}
                                            >
                                                <i
                                                    className={`icon ${font}-${icon.class}`}
                                                    style={{ fontSize: '20px' }}
                                                ></i>
                                            </div>
                                        ))}
                            </div>
                        </div>
                        </div>
                    );
                })}
            </div>
        </>
    );
};

export default IconManager;
